import { Vector3 } from '../../types/Vector3'
import { Quaternion } from '../../types/Quaternion'
import { LayoutInterface, AlignmentMode } from '../FieldTypes'
import { FieldParameters } from '../FieldParameters'
import { CurveUtils } from '../../../math/CurveUtils'
import { VectorUtils } from '../../../math/VectorUtils'
import { IndexTransforms } from '../../../math/IndexTransforms'

export class HexLayout implements LayoutInterface {
  // ==================== POSITION CALCULATION ====================
  
  /**
   * Calculate position for object at given index
   */
  public computePosition(index: number, totalCount: number, params: FieldParameters): Vector3 {
    // Convert index to hex coordinates
    const coords = IndexTransforms.indexToHex(index)
    
    // Calculate spacing
    const spacing = this.calculateSpacing(params, totalCount)
    
    // Get position on hex grid
    const position = CurveUtils.getPointOnHex(
      new Vector3(), // Center at origin
      spacing,
      coords.ring,
      coords.positionInRing
    )
    
    return position
  }
  
  // ==================== ORIENTATION CALCULATION ====================
  
  /**
   * Calculate orientation for object at given index using 2D surface mathematics
   */
  public computeOrientation(index: number, totalCount: number, params: FieldParameters): Quaternion {
    const coords = IndexTransforms.indexToHex(index)
    const position = this.computePosition(index, totalCount, params)
    
    // For hex coordinates, we need to map ring/position to U/V parameters
    // This is more complex than grid but follows similar principles
    const maxRing = Math.max(1, Math.floor(Math.sqrt(totalCount / 6))) // Estimate max ring
    const uParameter = coords.ring / maxRing // Ring as radial parameter
    const vParameter = coords.positionInRing / (6 * coords.ring || 1) // Position in ring as angular parameter
    
    // Use the improved 2D surface orientation calculation with existing enums
    const { right, up, forward } = CurveUtils.calculate2DSurfaceOrientation(
      'plane', // Hex grid is also on a plane surface
      position,
      params.fieldDirection, // Plane normal
      null, // No sphere center for planes
      uParameter, // U coordinate (0-1) - radial direction
      vParameter, // V coordinate (0-1) - angular direction
      params.alignmentMode, // Already AlignmentMode enum
      params.upVectorMode // Already UpVectorMode enum
    )
    
    // Convert to quaternion using lookDir
    return VectorUtils.lookRotation(forward, up)
  }
  
  // ==================== TANGENT CALCULATION ====================
  
  /**
   * Calculate tangent vector (direction along hex)
   */
  public computeTangent(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const coords = IndexTransforms.indexToHex(index)
    return this.getHexDirection(coords, params)
  }
  
  // ==================== NORMAL CALCULATION ====================
  
  /**
   * Calculate normal vector (perpendicular to hex)
   */
  public computeNormal(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const position = this.computePosition(index, totalCount, params)
    const center = new Vector3()
    return VectorUtils.getInwardNormal(position, center)
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Calculate spacing between objects
   */
  private calculateSpacing(params: FieldParameters, totalCount: number): number {
    if (params.useObjectSpacing && params.objectBounds) {
      // Use object bounds for spacing
      const objectSize = params.objectBounds.size
      const maxSize = Math.max(objectSize.x, objectSize.y, objectSize.z)
      return maxSize * params.spacingMultiplier
    } else {
      // Use fixed cell size plus padding
      return params.cellSize + params.cellSpacing
    }
  }
  
  /**
   * Get main directional vector of the layout (hex plane normal)
   */
  public getDirectionalVector(params: FieldParameters): Vector3 {
    return params.fieldDirection.clone().normalize()
  }
  
  /**
   * Get hex direction for alignment
   */
  private getHexDirection(coords: { ring: number; positionInRing: number }, params: FieldParameters): Vector3 {
    if (coords.ring === 0) {
      // Center point - use custom alignment or default
      return params.customAlignmentVector.length() > 0 
        ? params.customAlignmentVector.clone().normalize()
        : new Vector3(1, 0, 0)
    }
    
    // Calculate direction towards next position in ring
    const currentAngle = (coords.positionInRing / (coords.ring * 6)) * Math.PI * 2
    const nextAngle = currentAngle + (1 / (coords.ring * 6)) * Math.PI * 2
    
    const direction = new Vector3(
      Math.cos(nextAngle) - Math.cos(currentAngle),
      0,
      Math.sin(nextAngle) - Math.sin(currentAngle)
    )
    
    return direction.normalize()
  }
  
  // ==================== ADVANCED FEATURES ====================
  
  /**
   * Get hex bounds
   */
  public getHexBounds(params: FieldParameters, totalCount: number): { 
    min: Vector3
    max: Vector3
    center: Vector3
    radius: number
  } {
    const spacing = this.calculateSpacing(params, totalCount)
    const maxRing = this.getMaxRing(totalCount)
    const radius = maxRing * spacing
    
    return {
      min: new Vector3(-radius, 0, -radius),
      max: new Vector3(radius, 0, radius),
      center: new Vector3(0, 0, 0),
      radius: radius
    }
  }
  
  /**
   * Get maximum ring number for given total count
   */
  public getMaxRing(totalCount: number): number {
    if (totalCount <= 1) return 0
    
    let ring = 1
    let totalPoints = 1 // Center point
    
    while (totalPoints < totalCount) {
      totalPoints += ring * 6
      ring++
    }
    
    return ring - 1
  }
  
  /**
   * Get all indices in a specific ring
   */
  public getRingIndices(ring: number): number[] {
    if (ring === 0) return [0]
    
    const indices: number[] = []
    const startIndex = IndexTransforms.getTotalHexPoints(ring - 1)
    const pointsInRing = ring * 6
    
    for (let i = 0; i < pointsInRing; i++) {
      indices.push(startIndex + i)
    }
    
    return indices
  }
  
  /**
   * Get neighbors of a hex position
   */
  public getNeighbors(index: number, params: FieldParameters): number[] {
    const coords = IndexTransforms.indexToHex(index)
    const neighborCoords = IndexTransforms.getHexNeighbors(coords)
    
    return neighborCoords
      .map(coord => IndexTransforms.hexToIndex(coord.ring, coord.positionInRing))
      .filter(idx => idx >= 0) // Filter out invalid indices
  }
  
  /**
   * Find closest hex point
   */
  public findClosestPoint(targetPoint: Vector3, totalCount: number, params: FieldParameters): {
    point: Vector3
    index: number
    distance: number
    coords: { ring: number; positionInRing: number }
  } {
    let minDistance = Infinity
    let closestPoint = new Vector3()
    let closestIndex = 0
    let closestCoords = { ring: 0, positionInRing: 0 }
    
    // Check all hex positions
    for (let i = 0; i < totalCount; i++) {
      const point = this.computePosition(i, totalCount, params)
      const distance = targetPoint.distanceTo(point)
      
      if (distance < minDistance) {
        minDistance = distance
        closestPoint = point
        closestIndex = i
        closestCoords = IndexTransforms.indexToHex(i)
      }
    }
    
    return {
      point: closestPoint,
      index: closestIndex,
      distance: minDistance,
      coords: closestCoords
    }
  }
  
  /**
   * Get distance between two hex positions
   */
  public getHexDistance(index1: number, index2: number): number {
    const coords1 = IndexTransforms.indexToHex(index1)
    const coords2 = IndexTransforms.indexToHex(index2)
    
    return IndexTransforms.hexDistance(coords1, coords2)
  }
  
  /**
   * Check if index is on hex border
   */
  public isOnBorder(index: number, totalCount: number): boolean {
    const coords = IndexTransforms.indexToHex(index)
    const maxRing = this.getMaxRing(totalCount)
    
    return coords.ring === maxRing
  }
  
  /**
   * Get hex center index
   */
  public getCenterIndex(): number {
    return 0 // Center is always at index 0
  }
  
  /**
   * Get spiral traversal order (ring by ring)
   */
  public getSpiralTraversal(totalCount: number): number[] {
    const indices: number[] = []
    const maxRing = this.getMaxRing(totalCount)
    
    for (let ring = 0; ring <= maxRing; ring++) {
      const ringIndices = this.getRingIndices(ring)
      indices.push(...ringIndices)
    }
    
    return indices
  }
  
  /**
   * Get concentric rings
   */
  public getConcentricRings(totalCount: number): number[][] {
    const rings: number[][] = []
    const maxRing = this.getMaxRing(totalCount)
    
    for (let ring = 0; ring <= maxRing; ring++) {
      const ringIndices = this.getRingIndices(ring)
      if (ringIndices.length > 0) {
        rings.push(ringIndices)
      }
    }
    
    return rings
  }
  
  /**
   * Get hex side indices (for directional effects)
   */
  public getHexSideIndices(ring: number, side: number): number[] {
    if (ring === 0) return [0]
    
    const indices: number[] = []
    const pointsPerSide = ring
    const startIndex = IndexTransforms.getTotalHexPoints(ring - 1)
    const sideStartIndex = startIndex + side * pointsPerSide
    
    for (let i = 0; i < pointsPerSide; i++) {
      indices.push(sideStartIndex + i)
    }
    
    return indices
  }
  
  /**
   * Get hex pattern type at index
   */
  public getHexPatternType(index: number): 'center' | 'inner' | 'outer' | 'corner' {
    const coords = IndexTransforms.indexToHex(index)
    
    if (coords.ring === 0) return 'center'
    if (coords.ring === 1) return 'inner'
    
    // Check if it's a corner (at side boundaries)
    const pointsPerSide = coords.ring
    const isCorner = coords.positionInRing % pointsPerSide === 0
    
    return isCorner ? 'corner' : 'outer'
  }
  
  /**
   * Calculate hex density (points per area)
   */
  public getHexDensity(params: FieldParameters, totalCount: number): number {
    const bounds = this.getHexBounds(params, totalCount)
    const area = Math.PI * bounds.radius * bounds.radius
    return totalCount / area
  }
  
  /**
   * Get optimal hex size for given area
   */
  public getOptimalHexSize(targetArea: number, totalCount: number): number {
    const density = totalCount / targetArea
    const radius = Math.sqrt(totalCount / (Math.PI * density))
    return radius / this.getMaxRing(totalCount)
  }
} 